import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import KOutlineBg from '../KOutlineBg';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const stages = [
  { num: '01', title: 'Understand the organisation', desc: 'Learn how the business works, its structure, pressures and priorities.' },
  { num: '02', title: 'Gather evidence', desc: 'Surveys, focus groups, leadership conversations and workplace data.' },
  { num: '03', title: 'Identify pressure', desc: 'Find where culture is creating risk and what may be driving it.' },
  { num: '04', title: 'Agree priorities', desc: 'Work with leaders to decide what matters most and where to start.' },
  { num: '05', title: 'Support the response', desc: 'Training, development or ongoing support matched to the findings.' },
  { num: '06', title: 'Measure and learn', desc: 'Track whether the changes are working and adjust where needed.' },
];

const HowWeWorkSection = () => {
  return (
    <section id="how-we-work" className="py-20 md:py-28 bg-carbon relative overflow-hidden" data-testid="how-we-work-section">
      <KOutlineBg />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="max-w-3xl mb-12" {...fadeInUp}>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-6 tracking-tight">
            A CLEAR ROUTE FROM <span className="yellow-highlight text-carbon">CONCERN</span> TO <span className="yellow-highlight text-carbon">ACTION</span>
          </h2>
          <p className="text-base text-white/75 leading-relaxed">
            The exact work changes from one organisation to another, but the principle stays the
            same: understand before acting.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {stages.map((stage, i) => (
            <motion.div
              key={stage.num}
              className="bg-carbon p-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
            >
              <span className="text-golden font-heading font-bold text-2xl block mb-3">{stage.num}</span>
              <h3 className="text-base font-heading font-semibold text-white mb-2">{stage.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{stage.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div className="mt-10" {...fadeInUp}>
          <Link
            to="/how-we-work"
            className="bg-golden text-carbon px-7 py-3.5 font-semibold text-sm hover:bg-golden/90 transition-colors inline-flex items-center"
            data-testid="how-we-work-cta"
          >
            See how we work
            <ArrowRight className="ml-2 w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default HowWeWorkSection;
